import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from 'roo-ui/components';
import { usePrevious } from 'react-use';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Image from 'components/Image';
import { useTransition } from 'hooks/useTransition';
import { animationKeyframes } from 'lib/animations';

const ANIMATION_DURATION = 250;

const ImageWrapper = styled(Image)`
  display: table;
  & > img {
    margin: 0 auto;
  }
`;

const AnimationWrapper = styled(Box)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  left: 0;
  right: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  ${({ transitionState, direction }) =>
    transitionState === 'exited' &&
    direction === 'slideLeftOut' &&
    css`
      left: -100%;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'exiting' &&
    direction === 'slideLeftOut' &&
    css`
      left: 0;
      animation: ${animationKeyframes.slideLeft} ease ${ANIMATION_DURATION}ms;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'exited' &&
    direction === 'slideRightOut' &&
    css`
      left: 100%;
      right: 0;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'exiting' &&
    direction === 'slideRightOut' &&
    css`
      left: 0;
      animation: ${animationKeyframes.slideRight} ease ${ANIMATION_DURATION}ms;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'entered' &&
    direction === 'slideLeftIn' &&
    css`
      left: 0;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'entering' &&
    direction === 'slideLeftIn' &&
    css`
      left: 100%;
      animation: ${animationKeyframes.slideLeft} ease ${ANIMATION_DURATION}ms;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'entered' &&
    direction === 'slideRightIn' &&
    css`
      left: 0;
    `};

  ${({ transitionState, direction }) =>
    transitionState === 'entering' &&
    direction === 'slideRightIn' &&
    css`
      left: -100%;
      animation: ${animationKeyframes.slideRight} ease ${ANIMATION_DURATION}ms;
    `};
`;

const SlidingImage = ({ src, alt, direction, onAnimationComplete, onImageLoaded, open, height }) => {
  const [transitionToOpen, setTransitionToOpen] = useState(false);
  const transitionState = useTransition({ transition: transitionToOpen, duration: ANIMATION_DURATION });

  const prevTransitionState = usePrevious(transitionState);
  const prevSrc = usePrevious(src);

  useEffect(() => {
    setTransitionToOpen(open);
  }, [open, setTransitionToOpen]);

  useEffect(() => {
    if (onAnimationComplete && transitionState === 'exited' && prevTransitionState === 'exiting') {
      onAnimationComplete();
    }
  }, [transitionState, prevTransitionState, onAnimationComplete]);

  const onLoad = useCallback(() => {
    if (onImageLoaded && src !== prevSrc) {
      onImageLoaded();
    }
  }, [onImageLoaded, src, prevSrc]);

  if (!src) {
    return null;
  }

  return (
    <AnimationWrapper transitionState={transitionState} direction={direction}>
      <ImageWrapper maxWidth="100%" maxHeight="100%" width="auto" height={height} src={src} alt={alt} cover={false} onLoad={onLoad} />
    </AnimationWrapper>
  );
};

SlidingImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  direction: PropTypes.string,
  onAnimationComplete: PropTypes.func,
  onImageLoaded: PropTypes.func,
  open: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};

SlidingImage.defaultProps = {
  src: undefined,
  alt: undefined,
  direction: undefined,
  onAnimationComplete: undefined,
  onImageLoaded: undefined,
  open: false,
  height: '100%',
};

export default SlidingImage;
